import type { TransitionProps } from '@mui/material/transitions';
import useForkRef from '@mui/utils/useForkRef';
import type { Placement } from '@popperjs/core';
import type { CSSProperties, ForwardedRef, ReactElement, Ref } from 'react';
import { cloneElement, forwardRef, useRef } from 'react';
import type { TransitionStatus } from 'react-transition-group';
import { Transition } from 'react-transition-group';
import type { TimeoutProps } from 'react-transition-group/Transition';

function reflow(node: HTMLElement | null) {
	return node?.scrollTop;
}

const duration = {
	enter: 500,
	exit: 300,
} as const satisfies TransitionProps['timeout'];
export const DROPDOWN_TRANSITION_DURATION = duration;

const transitionStyle = (
	status: TransitionStatus,
	placement?: Placement
): CSSProperties => ({
	transformOrigin: placement?.includes('top')
		? 'bottom'
		: placement?.includes('left')
			? 'right'
			: placement?.includes('right')
				? 'left'
				: 'top',
	willChange: 'opacity, pointer-events, transform, visibility',
	transitionProperty: 'opacity, pointer-events, transform, visibility',

	transitionDuration: `${duration.exit}ms`,
	transitionTimingFunction: 'ease-in',

	opacity: 0,
	pointerEvents: 'none',
	transform:
		placement?.includes('left') || placement?.includes('right')
			? 'rotateY(-100deg)'
			: 'rotateX(-100deg)',
	visibility: 'hidden',

	...(status === 'entering' || status === 'entered'
		? {
				transitionTimingFunction: 'cubic-bezier(.18, .88, .32, 1.28)',
				transitionDuration: `${duration.enter}ms`,

				opacity: 1,
				pointerEvents: 'initial',
				transform:
					placement?.includes('left') || placement?.includes('right')
						? 'rotateY(0deg)'
						: 'rotateX(0deg)',
				visibility: 'visible',
			}
		: {}),
});

export interface DropdownTransitionProps extends TransitionProps {
	placement?: Placement;
	children: ReactElement & { ref?: Ref<unknown> };
}

/**
 * This should implement the same API as Material UI's transition components.
 */
export const DropdownTransition = forwardRef(function DropdownTransitionRender(
	{
		onEnter,
		onEntered,
		onEntering,
		onExit,
		onExited,
		onExiting,
		addEndListener: addEndListenerProp,
		placement,
		children,
		...other
	}: DropdownTransitionProps,
	forwardedRef: ForwardedRef<HTMLElement>
) {
	const ref = useRef<HTMLElement>(null);

	const handleRef = useForkRef(children.ref, forwardedRef, ref);

	// Define this separately so that we can narrow its type. The type of
	// `Transition` is hard to work with directly.
	const transitionProps: TimeoutProps<HTMLElement> = {
		appear: true,
		...other,
		nodeRef: ref,
		onEnter: (isAppearing: boolean) => {
			// Make sure the CSS engine picks up the initial state so that the
			// animation always plays from the start.
			reflow(ref.current);

			onEnter?.(ref.current!, isAppearing);
		},
		onEntering: (isAppearing: boolean) => {
			onEntering?.(ref.current!, isAppearing);
		},
		onExit: () => {
			onExit?.(ref.current!);
		},
		onExiting: () => {
			onExiting?.(ref.current!);
		},
		onExited: () => {
			onExited?.(ref.current!);
		},
		addEndListener: (done) => {
			addEndListenerProp?.(ref.current!, done);
		},
		timeout: duration,
	};

	return (
		<Transition {...transitionProps}>
			{(status, childProps) =>
				cloneElement(children, {
					style: {
						...transitionStyle(status, placement),
						...children.props.style,
					},
					ref: handleRef,
					...childProps,
				})
			}
		</Transition>
	);
});
